import { useParams, Link } from 'react-router-dom';
import { sportCategories, sportArticles } from '@/mocks/sportsData';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = sportCategories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = sportArticles.filter((a) => a.category === slug);
  const categoryName = category.name;
  const catDescriptions: Record<string, string> = {
    football: '全球最受欢迎的体育运动，涵盖英超、西甲、欧冠等顶级联赛实时数据与深度分析。',
    basketball: 'NBA、CBA、欧洲篮球联赛等赛事全覆盖，实时比分、球员数据、战术分析一应俱全。',
    tennis: '四大满贯、ATP、WTA巡回赛全程跟踪，专业数据统计与比赛预测分析。',
    esports: 'LOL、DOTA2、CS2等顶级电竞赛事直播与数据，紧跟电竞产业发展脉搏。',
    baseball: 'MLB、NPB等职业棒球联赛赛事数据，专业投打数据分析与比赛走势预测。',
    racing: 'F1、MotoGP、WRC等顶级赛车赛事，排位赛成绩、正赛实时数据追踪。',
  };

  return (
    <>
      <SEOHead
        title={`${categoryName}赛事 - 开体育在线`}
        description={`开体育在线${categoryName}频道提供${catDescriptions[slug] || ''}实时赛事直播、数据分析与专业解读。`}
        canonicalUrl={`https://www.kaitiyu.com/category/${slug}`}
      />
      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: categoryName },
        ]}
      />

      <main className="pt-4">
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-primary-100 rounded-lg">
                <i className={`${category.icon} text-2xl md:text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{categoryName}</h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {sportCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.publishDate}</span>
                        <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-article-line text-4xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}